import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageSquare, BookOpen, Send, Pencil, FileText, PenTool, UserPlus } from "lucide-react";
import QuotationsPage from "@/pages/QuotationsPage";
import InstructorSignature from "@/components/InstructorSignature";
import BulkEnrollmentUpload from "@/components/BulkEnrollmentUpload";

export default function TeacherPanel() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [courses, setCourses] = useState([]);
  const [answers, setAnswers] = useState({});
  const [tab, setTab] = useState(params.get("tab") || "tickets");
  const [sending, setSending] = useState({});

  const load = async () => {
    const [t, c] = await Promise.all([
      api.get("/tickets"),
      api.get("/courses"),
    ]);
    setTickets(t.data);
    setCourses(c.data.filter((x) => x.teacher_id === user.id));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submitAnswer = async (id) => {
    const text = answers[id];
    if (!text?.trim()) return;
    setSending({ ...sending, [id]: true });
    try {
      await api.post(`/tickets/${id}/answer`, { answer_text: text });
      setAnswers({ ...answers, [id]: "" });
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    } finally { setSending({ ...sending, [id]: false }); }
  };

  const openTickets = tickets.filter((t) => t.status === "open");
  const resolved = tickets.filter((t) => t.status === "resolved");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading font-black text-3xl sm:text-4xl text-marino tracking-tight">Panel del Instructor</h1>
      <p className="text-marino/70 mt-1">Hola {user?.name}, aquí puedes responder consultas y gestionar tus cursos.</p>

      <div className="mt-6 border-b border-gris/30 flex gap-6">
        <button data-testid="tab-tickets" onClick={() => setTab("tickets")} className={`pb-2 font-medium ${tab === "tickets" ? "text-naranja border-b-2 border-naranja" : "text-marino/60"}`}>
          Consultas ({openTickets.length})
        </button>
        <button data-testid="tab-courses" onClick={() => setTab("courses")} className={`pb-2 font-medium ${tab === "courses" ? "text-naranja border-b-2 border-naranja" : "text-marino/60"}`}>
          Mis cursos ({courses.length})
        </button>
        <button data-testid="tab-quotations" onClick={() => setTab("quotations")} className={`pb-2 font-medium inline-flex items-center gap-1.5 ${tab === "quotations" ? "text-naranja border-b-2 border-naranja" : "text-marino/60"}`}>
          <FileText className="w-4 h-4" /> Cotizaciones
        </button>
        <button data-testid="tab-signature" onClick={() => setTab("signature")} className={`pb-2 font-medium inline-flex items-center gap-1.5 ${tab === "signature" ? "text-naranja border-b-2 border-naranja" : "text-marino/60"}`}>
          <PenTool className="w-4 h-4" /> Mi firma
        </button>
        <button data-testid="tab-bulk" onClick={() => setTab("bulk")} className={`pb-2 font-medium inline-flex items-center gap-1.5 ${tab === "bulk" ? "text-naranja border-b-2 border-naranja" : "text-marino/60"}`}>
          <UserPlus className="w-4 h-4" /> Carga masiva
        </button>
      </div>

      {tab === "quotations" && <div className="mt-6"><QuotationsPage /></div>}
      {tab === "signature" && <div className="mt-6"><InstructorSignature /></div>}
      {tab === "bulk" && (
        <div className="mt-6 space-y-3">
          <p className="text-sm text-marino/70">
            Puedes cargar alumnos y generar certificados en lote <b>únicamente para los cursos que impartes</b>.
          </p>
          <BulkEnrollmentUpload endpoint="/teacher/bulk-enrollments" courses={courses} />
        </div>
      )}

      {tab === "tickets" && (
        <div className="mt-6 space-y-6">
          <section>
            <h2 className="font-heading font-bold text-marino text-xl mb-3 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-naranja" /> Pendientes</h2>
            {openTickets.length === 0 && <p className="text-marino/60">No hay consultas pendientes.</p>}
            <div className="space-y-3">
              {openTickets.map((t) => (
                <div key={t.id} data-testid={`teacher-ticket-${t.id}`} className="bg-white border border-gris/30 rounded-xl p-5">
                  <div className="flex justify-between text-xs text-marino/60 mb-1">
                    <span>{t.student_name} · {t.course_title}</span>
                    <span>{new Date(t.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-2 text-marino"><span className="font-semibold">Pregunta:</span> {t.question_text}</div>
                  <div className="mt-3 flex gap-2">
                    <textarea
                      data-testid={`answer-input-${t.id}`}
                      value={answers[t.id] || ""}
                      onChange={(e) => setAnswers({ ...answers, [t.id]: e.target.value })}
                      rows={2}
                      className="flex-1 border border-gris rounded-lg p-2 text-sm focus:border-marino focus:outline-none"
                      placeholder="Escribe tu respuesta..."
                    />
                    <button data-testid={`answer-send-${t.id}`} onClick={() => submitAnswer(t.id)} disabled={sending[t.id]} className="bg-naranja text-white px-4 py-2 rounded-lg font-medium hover:bg-naranjaHover disabled:opacity-60 self-end">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {resolved.length > 0 && (
            <section>
              <h3 className="font-heading font-bold text-marino/70 text-lg mb-3">Resueltas</h3>
              <div className="space-y-2">
                {resolved.slice(0, 10).map((t) => (
                  <div key={t.id} className="bg-white border border-gris/30 rounded-lg p-3">
                    <div className="text-xs text-marino/60">{t.student_name} · {t.course_title}</div>
                    <div className="text-sm text-marino mt-1">P: {t.question_text}</div>
                    <div className="text-sm text-naranja mt-1">R: {t.answer_text}</div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {tab === "courses" && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {courses.map((c) => (
            <div key={c.id} data-testid={`teacher-course-${c.id}`} className="bg-white border border-gris/30 rounded-xl overflow-hidden shadow-sm flex flex-col">
              {c.thumbnail && <img src={c.thumbnail} className="w-full h-32 object-cover" alt="" />}
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-heading font-bold text-marino">{c.title}</h3>
                <p className="text-sm text-marino/70 mt-1 line-clamp-2 flex-1">{c.description}</p>
                <div className="mt-3 flex items-center gap-2 text-xs text-marino/60">
                  <BookOpen className="w-4 h-4" /> {c.duration_hours}h
                </div>
                <Link
                  to={`/instructor/courses/${c.id}`}
                  data-testid={`edit-course-${c.id}`}
                  className="mt-4 bg-naranja text-white text-center px-3 py-2 rounded-lg text-sm font-medium hover:bg-naranjaHover inline-flex items-center justify-center gap-1"
                >
                  <Pencil className="w-4 h-4" /> Editar contenido
                </Link>
              </div>
            </div>
          ))}
          {courses.length === 0 && <p className="text-marino/60 col-span-full">No tienes cursos asignados. Contacta al administrador.</p>}
        </div>
      )}
    </div>
  );
}
